export type CrmDateField = 'entry' | 'won' | 'lost' | 'lead_arrival';

export const DEFAULT_CRM_DATE_FIELD: CrmDateField = 'entry';

/**
 * Labels stay in pt-BR as a fallback and each option carries its i18n key, resolved by the
 * component. A module-level array cannot call `t()` — it is built at import time, before the
 * account language is known.
 */
export const CRM_DATE_FIELD_OPTIONS: {
  value: CrmDateField;
  label: string;
  labelKey: string;
  fromLabel: string;
  fromLabelKey: string;
  toLabel: string;
  toLabelKey: string;
}[] = [
  {
    value: 'entry',
    label: 'Data de entrada',
    labelKey: 'crmFilters.entry',
    fromLabel: 'Entrada a partir de',
    fromLabelKey: 'crmFilters.entryFrom',
    toLabel: 'Entrada até',
    toLabelKey: 'crmFilters.entryTo',
  },
  {
    value: 'won',
    label: 'Data de ganho',
    labelKey: 'crmFilters.won',
    fromLabel: 'Ganho a partir de',
    fromLabelKey: 'crmFilters.wonFrom',
    toLabel: 'Ganho até',
    toLabelKey: 'crmFilters.wonTo',
  },
  {
    value: 'lost',
    label: 'Data de perda',
    labelKey: 'crmFilters.lost',
    fromLabel: 'Perda a partir de',
    fromLabelKey: 'crmFilters.lostFrom',
    toLabel: 'Perda até',
    toLabelKey: 'crmFilters.lostTo',
  },
  {
    value: 'lead_arrival',
    label: 'Chegada do lead',
    labelKey: 'crmFilters.leadArrival',
    fromLabel: 'Chegada a partir de',
    fromLabelKey: 'crmFilters.leadArrivalFrom',
    toLabel: 'Chegada até',
    toLabelKey: 'crmFilters.leadArrivalTo',
  },
];

export function getCrmDateFieldOption(dateField: CrmDateField) {
  return (
    CRM_DATE_FIELD_OPTIONS.find((option) => option.value === dateField) ??
    CRM_DATE_FIELD_OPTIONS[0]
  );
}

/** Resolve the date used for CRM period/range filters. */
export function getOpportunityFilterDate(
  opp: {
    created_at: string;
    sale_date?: string | null;
    closed_at?: string | null;
    contact_created_at?: string | null;
  },
  dateField: CrmDateField
): Date | null {
  let raw: string | null | undefined;

  switch (dateField) {
    case 'entry':
      raw = opp.created_at;
      break;
    case 'won':
      raw = opp.sale_date || opp.closed_at;
      break;
    case 'lost':
      raw = opp.closed_at;
      break;
    case 'lead_arrival':
      raw = opp.contact_created_at;
      break;
    default:
      raw = opp.created_at;
  }

  if (!raw) return null;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function opportunityNeedsContactInnerJoin(dateField: CrmDateField): boolean {
  return dateField === 'lead_arrival';
}

/**
 * Apply a lower-bound date filter on account_opportunities (and contact when needed).
 * `won` uses sale_date with fallback to closed_at (same rule as reports).
 */
export function applyOpportunityDateGte<T>(
  query: T,
  dateField: CrmDateField,
  fromIso: string
): T {
  const q = query as {
    gte: (column: string, value: string) => T;
    or: (filters: string) => T;
  };
  const quoted = `"${fromIso}"`;

  switch (dateField) {
    case 'entry':
      return q.gte('created_at', fromIso);
    case 'won':
      return q.or(
        `sale_date.gte.${quoted},and(sale_date.is.null,closed_at.gte.${quoted})`
      );
    case 'lost':
      return q.gte('closed_at', fromIso);
    case 'lead_arrival':
      return q.gte('contact.created_at', fromIso);
    default:
      return q.gte('created_at', fromIso);
  }
}
